// Englische Fassung von ../de/tasks.ts. Die Typ-Annotation ist die
// Paritätsprüfung: ein fehlender Key ist TS2741, ein überzähliger TS2353.
//
// Datums-FORMATE bleiben deutsch (docs/i18n.md §2) — übersetzt ist hier nur die
// Beschreibung des Formats in den Vorlese-Beschriftungen (TT.MM.JJJJ →
// DD.MM.YYYY beschreibt dieselbe Eingabe).
import type { TasksKatalog } from "../de/tasks";

export const tasks: TasksKatalog = {
  seitenTitel: "Tasks",
  offeneAufgaben: (n: number) => (n === 1 ? "1 open task" : `${n} open tasks`),
  keineAufgaben: "No tasks",

  reiter: {
    kanban: "Kanban",
    kalender: "Calendar",
    zeitachse: "Timeline",
  },

  kanban: {
    titel: "Kanban",
    spalteIngest: "Ingest",
    spalteOffen: "Open",
    spalteInArbeit: "In progress",
    spalteReview: "Review",
    spalteErledigt: "Done",
    spalteProjekt: "Project",

    ladenFehlgeschlagen: "Loading failed",
    sfNichtVerschiebbar:
      "Salesforce tasks cannot be moved — edit them in Salesforce.",
    workstreamReihenfolgeFehler: (grund: string) =>
      `Could not save the workstream order: ${grund} — reloading`,
    reihenfolgeFehler: (grund: string) => `Could not save the order: ${grund} — reloading`,
    speichernFehler: (grund: string) => `Could not save: ${grund} — reloading`,

    aufgabeZuweisen: "Assign task",
    filter: "Filter",
    filterUmschalten: "Toggle filters",
    neuLaden: "reload",
    projektFiltern: "Filter projects …",
    projektFilter: "Project filter",
    pilleMeineTasks: "my tasks",
    pilleVonMirZugewiesen: "Assigned by me",
    pilleDone: "Done",
    pilleSalesforce: "Salesforce",
    zuweisen: "Assign",
    nurMeine: "Only mine",
    vonMirZugewiesenTitel: "Tasks I assigned to someone else",
    vonMirZugewiesen: "Assigned by me",
    erledigte: "Completed",
    salesforce: "Salesforce",

    leerEigeneTitel: "No tasks of your own",
    leerEigeneHinweis:
      "Nothing is assigned to you right now — but there are tasks from others.",
    alleAufgabenZeigen: "Show all tasks",
    leerGefiltertTitel: (filter: string) => `No tasks for “${filter}”`,
    leerHinweis: "Adjust the filters above or create a new task.",
    filterZuruecksetzen: "Reset filter",

    projektUmsortieren: "Reorder project (drag)",
    projektUmsortierenAria: (projekt: string) => `Reorder project ${projekt}`,
    zieleEinAusklappen: "Show/hide project goals",
    ziele: "Goals",
    statusReiterAria: (projekt: string) => `${projekt} status tabs`,
    aufgabeHinzufuegen: "Add task",

    ideeEinwerfen: "Drop an idea",
    intakeLeer: "(empty)",
    intakeFragen: (n: number) => (n === 1 ? `${n} question` : `${n} questions`),
    laedt: "loading…",

    workstreamUmsortieren: "Reorder workstream (drag)",
    workstreamUmsortierenAria: (name: string) => `Reorder workstream ${name}`,
    workstreamEinklappen: "Collapse workstream",
    workstreamAusklappen: "Expand workstream",
    workstreamKontextOeffnen: "Open workstream context",
    ohneWorkstream: "no workstream",
    workstreamFallback: "Workstream",
    prioritaetTitel: (p: number) => `Priority ${p}`,

    ueberfaellig: "overdue",
    faelligAm: "Due on",
    erinnerungAm: (datum: string) => `Reminder: ${datum}`,
    inSalesforceOeffnen: "Open in Salesforce",
    zugewiesenVon: (wer: string) => `from ${wer}`,
    zugewiesenAn: (wer: string) => `→ ${wer}`,
    wartetAufAntwort: "Waiting for your answer",
    brauchtEntscheidung: "needs a decision",
    rework: (runde: number) => `Rework #${runde}`,
    wartetAufTasks: (n: number) =>
      n === 1 ? `waiting for ${n} task` : `waiting for ${n} tasks`,
  },

  ziele: {
    laden: "Loading goals…",
    projektZiel: "Project goal",
    bearbeiten: "edit",
    platzhalter: "Overarching goal of the project (1-3 sentences)…",
    keinZiel: "no project goal set yet",
    architektur: "Architecture",
    workstreams: "Workstreams",
    workstreamOhneZiel: " — no goal (set it via 📓 workstream doc)",
  },

  workstreamDoku: {
    verwerfenFrage: "Discard unsaved changes?",
    verwerfenText: "The editor content will be lost.",
    ueberschreibenFrage: "The current content will be overwritten — continue?",
    ueberschreiben: "Overwrite",
    kopf: (name: string) => `Workstream context: ${name}`,
    kopfUnterzeile:
      "Living workstream doc — loop sessions read it in PERCEIVE and update it in WRAP-UP.",
    prio: "Prio",
    prioAria: "Workstream priority (1 highest, 100 lowest)",
    prioTitel:
      "Workstream priority 1..100 (1=highest). Affects next-claimable + sorting on the board.",
    zeilen: (ist: number, budget: number) => `${ist}/${budget} lines`,
    budgetUeberschritten: "Soft budget exceeded — condense at the next wrap-up",
    budget: "Soft budget 80 lines",
    schliessenAria: "Close",
    vorschau: "Preview",
    markdown: "Markdown",
    vorlageEinfuegen: "Insert template",
    leer: "(empty)",
    editorPlatzhalter:
      "# Workstream-CLAUDE.md is empty — press 'Insert template' in the top right to start.",
    editorAria: "Workstream markdown editor",
    handoffs: (n: number) => `Handoffs (${n})`,
    ungespeichert: "unsaved",
    zuletztGeaendert: (wann: string) => `last changed ${wann}`,
    schliessen: "Close",
    workstreamNamePlatzhalter: "<workstream name>",
  },

  session: {
    stale: "stale",
    staleTitel: (min: number) => `stale — no sign of life for ${min} min`,
    ok: "ok",
    wrapSoon: "wrap soon",
    wrapNow: "wrap NOW",
    erledigt: (n: number) => `${n} done`,
    letztesUpdate: "Minutes since the last board update",
    live: "live",
  },

  zeitachse: {
    titel: "Timeline",
    mobilTitel: "Timeline works best on a desktop",
    mobilText:
      "The Gantt view (project sidebar + task tree + time axis) needs more room than a phone screen offers. Open this page on a desktop or on a tablet in landscape mode.",
    zuKanban: "To Kanban",
    zuKalender: "To calendar",
    mobilFuss: "Both views support mobile use.",
    // Der deutsche Bestandstext lautet unverändert „1 Aufgaben" (keine
    // Plural-Behandlung) — die englische Fassung bekommt sie, weil „1 tasks"
    // im Englischen als Fehler gelesen wird.
    kopf: (n: number, von: string, bis: string) =>
      `${n} ${n === 1 ? "task" : "tasks"} · ${von} to ${bis} · grouped by project`,
    projektFilter: "Project filter",
    alleProjekte: "All projects",
    erledigteAusblenden: "Hide completed",
    spalteProjektAufgabe: "Project / task",
    heute: "Today",
    leer: "No tasks in the visible period.",
    ohneProjekt: "no project",
    offen: "open",
    balkenOffen: (titel: string, start: string) =>
      `${titel} · ${start} → open (no end date)`,
    balken: (titel: string, start: string, ende: string) =>
      `${titel} · ${start} → ${ende}`,
    status: {
      new: "New",
      open: "Open",
      ongoing: "Ongoing",
      in_progress: "In progress",
      done: "Done",
      closed: "Closed",
      lost: "Lost",
      "on hold": "On hold",
      feedback: "Feedback",
    },
  },

  wizard: {
    titelBearbeiten: "Edit task",
    titelZuweisen: "Assign task",
    titelNeu: "New task",
    aktionSpeichern: "Save",
    aktionZuweisen: "Assign",
    aktionAnlegen: "Create task",
    loeschenFrage: (titel: string) => `Delete task “${titel}”?`,

    dateiTypNichtUnterstuetzt: "File type not supported",
    dateiTypErlaubt: "Allowed: PDF, Markdown, TXT",
    dateiAblegen: "Drop a file here (PDF, Markdown, TXT)",
    zugewiesenToast: (n: number) =>
      n === 1 ? `Assigned to ${n} colleague` : `Assigned to ${n} colleagues`,
    speichernFehlgeschlagen: "Could not save the task",
    zuweisenFehlgeschlagen: "Could not assign the task",
    anlegenFehlgeschlagen: "Could not create the task",
    loeschenFehlgeschlagen: "Could not delete the task",

    feldTitel: "Title",
    titelPlatzhalter: "What needs to be done?",
    feldBeschreibung: "Description",
    optional: "(optional)",
    beschreibungPlatzhalter: "Details, acceptance criteria, notes …",

    zuweisenAn: "Assign to",
    zuweisenAnHinweis: "(optional — empty = for me)",
    ganzeAbteilung: "Assign a whole department",
    alleAbteilungen: "All departments",
    alleAbteilungenEntfernen: "Remove all departments",
    abteilungEntfernen: (abteilung: string) => `Remove ${abteilung}`,
    persoenlich: "Personal",
    persoenlichVorNicht: " — the recipient may ",
    persoenlichNicht: "not",
    persoenlichNachNicht: " pass this task on",
    persoenlichStandard: " (default: can be passed on)",

    start: "Start",
    startAria: "Start date (DD.MM.YYYY)",
    faellig: "Due",
    faelligAria: "Due date (DD.MM.YYYY)",

    erinnerung: "Reminder",
    erinnerungTitel: "First/only reminder (DD.MM.YYYY)",
    erinnerungAria: "Reminder date (DD.MM.YYYY)",
    intervallKeine: "No reminder",
    intervallEinmalig: "Once (on one day)",
    intervallTaeglich: "Daily",
    intervallWoechentlich: "Weekly",
    intervallZweiwoechentlich: "Every 2 weeks",
    intervallMonatlich: "Monthly",
    intervallCustom: "Custom (every N days)",
    alle: "Every",
    tageErinnern: "days",

    workstream: "Workstream",
    keinWorkstream: "— No workstream —",

    anhaenge: "Attachments",
    linkPlatzhalter: "Insert a link (https://…)",
    link: "Link",
    linkEntfernen: "Remove link",
    laedt: "uploading…",
    datei: "File",
  },

  drawer: {
    modalTitel: "Task",
    laden: "loading…",

    sfSchreibgeschuetzt: "Salesforce task — read-only",
    sfHinweis: "Edit this task in Salesforce. Atlas only mirrors it.",
    sfOeffnen: "Open in Salesforce",

    ansichtUmschalten: "Switch view",
    ansichtKompakt: "Compact",
    ansichtDetail: "Detail",
    ansichtKompaktTitel: "Compact view",
    ansichtDetailTitel: "Detailed view",

    entscheidungZuKurz: "Please enter your decision as a short text (min. 3 characters).",
    erinnerungVerschoben: "Reminder postponed",
    weitergereichtAn: "Passed on to",
    linkAnhaengenFehler: "Could not attach the link",
    dokumentAnhaengenFehler: "Could not attach the document",
    anhangEntfernenFehler: "Could not remove the attachment",

    loeschenFrage: (titel: string) => `Delete task "${titel}"?`,
    loeschenAktion: "Delete task",

    brauchtEntscheidungFett: "Needs a human decision.",
    brauchtEntscheidungText:
      "The decider escalated (irreversible/external or rework cap reached) — set the status above (Open = release for implementation, Done = close).",

    feldTitel: "Title",
    titelAria: "Task title",
    status: "Status",
    statusOffen: "Open",
    statusInArbeit: "In progress",
    statusReview: "Review (check)",
    statusFeedback: "Feedback (waiting for me)",
    statusErledigt: "Done",
    prioritaet: "Priority",
    prioritaetTitel: "1 = highest, 100 = lowest; 50 = default",
    prioritaetAria: "Priority (1 highest, 100 lowest)",
    faelligAm: "Due on",
    faelligAria: "Due date (DD.MM.YYYY)",
    faelligTitel: "Due on (DD.MM.YYYY)",
    faelligGesperrt:
      "Only the creator can change the due date — you can postpone the reminder.",

    ziel: "Goal (one sentence, a clear statement)",
    zielAria: "Goal (one sentence, a clear statement)",
    zielPlatzhalter:
      "e.g. 'Director layer iter2: all LIFESTYLE slides use bullets_with_image with demographic pool rotation'",
    acceptance: "Acceptance criteria (one per line, measurable)",
    acceptancePlatzhalter:
      "- bullets_with_image rendered at pos 6+7\n- pool[0] and pool[1] are visibly different people\n- vision audit score >= 6.0",
    beschreibung: "Description / notes",
    beschreibungAria: "Description / Notes",
    beschreibungPlatzhalter: "—",

    unteraufgaben: "Subtasks",
    unteraufgabenStand: (fertig: number, gesamt: number) => `(${fertig}/${gesamt})`,
    unteraufgabe: "Subtask",
    unteraufgabePflicht: "A subtask needs a title, a goal and at least 1 acceptance criterion.",
    unteraufgabeFehler: "Could not create the subtask",
    unteraufgabeStatusFehler: "Could not change the subtask status",
    unteraufgabeWiederOeffnen: "Click: reopen",
    unteraufgabeErledigt: "Click: done",
    unteraufgabeTitelPlatzhalter: "Title of the subtask (short, imperative)",
    unteraufgabeTitelAria: "Subtask title",
    unteraufgabeZielPlatzhalter: "Goal (1 sentence — measurable)",
    unteraufgabeZielAria: "Subtask goal",
    unteraufgabeAcceptancePlatzhalter:
      "Acceptance criteria (one per line, BINARY checkable)\ne.g. - endpoint POST /xxx returns 201\n- test scripts/test_xxx.py green",
    unteraufgabeAcceptanceAria: "Subtask acceptance criteria",
    unteraufgabePrioAria: "Subtask priority",
    unteraufgabeLeer:
      "no subtasks. For multi-step goals: create the subtasks first, then start working.",

    workstream: "Workstream",
    workstreamAria: "Workstream assignment",
    keinWorkstream: "— No workstream —",
    workstreamNeuTitel: "Create a new workstream in this project",
    workstreamNeuAria: "Create a new workstream",
    workstreamNamePlatzhalter: "e.g. 'Director iteration', 'Cost tracking', 'Frontend polish'",
    workstreamNameAria: "New workstream name",
    workstreamFehler: "Could not create the workstream",

    fragt: (wer: string) => `${wer} asks`,
    freitextPlatzhalter: "optional: a further remark (stored together with your choice)…",
    freitextAria: "Free-text answer (optional)",

    eskalationKopf: (wer: string) => `Needs your decision — ${wer} escalated`,
    eskalationEmpfehlung: "The decider recommends:",
    entscheidungPflicht: "Your decision (required)",
    entscheidungAria: "Your decision",
    entscheidungPlatzhalter:
      "Instruction/reasoning — stored as a binding decision; the next session follows it…",
    entscheidungSenden: "Send decision & release",
    entscheidungSchliessen: "Close with this decision",
    entscheidungFuss:
      "“Release” → status open + highest priority, the loop picks it up next. “Close” → done, without further work.",
    mastermindKopf: "Needs your decision — the mastermind escalated to you",
    mastermindPlatzhalter:
      "What should happen? — stored as a binding decision; the next session follows it…",
    mastermindFuss:
      "“Release” → status open + highest priority, the loop picks it up next and reads your decision. “Close” → done.",
    mastermind: "Mastermind",
    mastermindRunde: (n: number) => `Round ${n}`,
    mastermindEskaliert: "escalated",
    mastermindAufgeloest: "resolved",
    fuerDenMenschen: "For the human",

    spec: "Spec",
    haengtAbVon: (n: number) =>
      n === 1 ? `Depends on ${n} task` : `Depends on ${n} tasks`,
    nonGoals: "Non-goals",
    verifikation: "Verification",
    visualQuality: "Visual quality criteria",
    scope: "Scope",

    review: (verdikt: string) => `Review: ${verdikt}`,
    reviewRunde: (runde: number, pruefer: string) => `Round ${runde} · ${pruefer}`,
    reviewsGesamt: (n: number) => ` · ${n} reviews in total`,
    reviewFix: (fix: string) => ` → Fix: ${fix}`,
    reviewZurueck: "Back to “Open” for rework — the loop claims it again.",

    evidence: (n: number) => `Evidence (${n})`,
    evidenceVergroessern: "Enlarge",

    activity: "Activity",
    dateiAnhaengen: "Attach a file",
    laedt: "uploading…",
    activityLeer:
      "No activity yet. As soon as a session works on this task, comments, results and questions appear here.",
    optionen: "Options: ",
    oeffnen: "open",

    zugewiesenVonAn: "Assigned by",
    zugewiesenAn: "to",
    weiterreichbar: "can be passed on",
    persoenlich: "personal",
    erinnerung: "Reminder",
    heuteFaellig: "🔔 due today",
    erinnerungKeine: "No reminder",
    erinnerungEinmalig: "once",
    erinnerungAlleTage: (n: number) => `every ${n} days`,
    erinnerungNaechste: (intervall: string, datum: string) =>
      `${intervall}, next on ${datum}`,
    intervallAria: "Reminder interval",
    intervallKeine: "None",
    intervallEinmalig: "Once",
    intervallTaeglich: "Daily",
    intervallWoechentlich: "Weekly",
    intervallZweiwoechig: "Every 2 weeks",
    intervallMonatlich: "Monthly",
    intervallCustom: "Custom",
    naechsteErinnerungAria: "Next reminder (DD.MM.YYYY)",
    naechsteErinnerungTitel: "Next/first reminder (DD.MM.YYYY)",
    alle: "Every",
    tage: "days",
    verschieben: "Postpone",
    dreiTage: "+3 days",
    eineWoche: "+1 week",
    verschiebenAria: "Postpone to (DD.MM.YYYY)",
    verschiebenTitel: "Postpone the reminder to (DD.MM.YYYY)",
    uebernehmen: "Apply",
    weiterreichen: "Pass on",
    weiterreichenTitel: "Pass on to another person",
    weiterreichenGesperrt: "Personal — cannot be passed on",
    uebergebenAn: (wer: string) => `Hand over to ${wer}`,

    anhaenge: "Attachments (docs & links)",
    dokument: "Document",
    anhangEntfernen: "Remove attachment",
    linkPlatzhalter: "Attach a link (https://…)",
    link: "Link",
    dateiAnhaengenKnopf: (datei: string) => `Attach “${datei}”`,
    datei: "File",
  },
};
